import { useState, useContext } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import ClearIcon from "@mui/icons-material/Clear";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import ThemeContext from "../lib/theme-context";
import MultiFilterPanel from "./MultiFilterPanel";
import CustomSingleTextField from "./textfields/singleRow";
import CustomSelect from "./select/index";

const FloatingSearch = ({ 
  value, 
  onChange, 
  filteredCount = null, 
  totalCount = null,
  multiFilters = [],
  onMultiFiltersChange = () => {},
  logicOperator = "or",
  onLogicOperatorChange = () => {},
  starFilters = {},
  onStarFiltersChange = () => {},
}) => {
  const theme = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value || "");
  const [activeTab, setActiveTab] = useState(0); // 0: Quick Search, 1: Multi Filter

  const handleOpen = () => {
    setIsOpen(true);
    setLocalValue(value || "");
  };

  const handleClose = () => {
    setIsOpen(false);
  };


  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleClear = () => {
    setLocalValue("");
    onChange("");
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  const getStarIcon = (rating, starValue) => {
    if (rating >= starValue) {
      return (
        <StarIcon
          style={{
            fontSize: "28px",
            color: "#ED9E0E",
            filter: "drop-shadow(0 2px 4px rgba(237, 158, 14, 0.3))",
          }}
        />
      );
    } else if (rating >= starValue - 0.5) {
      return (
        <StarHalfIcon
          style={{
            fontSize: "28px",
            color: "#ED9E0E",
            filter: "drop-shadow(0 2px 4px rgba(237, 158, 14, 0.3))",
          }}
        />
      );
    } else {
      return (
        <StarBorderIcon
          style={{
            fontSize: "28px",
            color: theme[0] === "dark" ? "#4b5563" : "#d1d5db",
          }}
        />
      );
    }
  };

  const handleStarClick = (filterKey, starValue, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const isHalf = clickX < rect.width / 2;
    const finalValue = isHalf ? starValue - 0.5 : starValue;
    
    onStarFiltersChange({
      ...starFilters,
      [filterKey]: starFilters[filterKey] === finalValue ? 0 : finalValue
    });
  };

  return (
    <>
      {/* Floating Search Icon */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 11, sm: 6 },
          left: { xs: 30, sm: 15 },
          right: "auto",
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={handleOpen}
          sx={{
            width: 44,
            height: 44,
            backgroundColor: theme[0] === "dark" ? "#4a5568" : "#6b7280",
            color: "#ffffff",
            boxShadow: "0 3px 8px rgba(0, 0, 0, 0.15)",
            borderRadius: "50%",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: theme[0] === "dark" ? "#5a6575" : "#4b5563",
              transform: "scale(1.05)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {/* Sleek Search Panel */}
      {isOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 84, // Position above the floating icon (20 + 44 + 20)
            left: { xs: 25, sm: 24 },
            right: "auto",
            width: { xs: "calc(100vw - 30px)", sm: 500, md: 600 },
            maxWidth: "calc(100vw - 50px)",
            backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
            border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            zIndex: 999,
            animation: "slideUp 0.3s ease-out",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          {/* Panel Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px 12px",
              borderBottom: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <SearchIcon 
                sx={{ 
                  fontSize: 20, 
                  color: theme[0] === "dark" ? "#1C83DE" : "#2563eb" 
                }} 
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                }}
              >
                Search Trades
              </span>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                "&:hover": {
                  backgroundColor: theme[0] === "dark" 
                    ? "rgba(255, 255, 255, 0.05)" 
                    : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}` }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  fontSize: "12px",
                  fontWeight: "500",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  minHeight: "40px",
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                },
              }}
            >
              <Tab label="Quick Search" />
              <Tab label="Advanced Filter" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 ? (
            // Quick Search Tab
            <Box sx={{ padding: "12px 20px 16px" }}>
              <CustomSingleTextField
                value={localValue}
                onChange={handleChange}
                onKeyDown={handleKeyPress}
                placeholder="Search"
                size="small"
                variant="outlined"
                autoFocus
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ 
                        fontSize: "18px",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: localValue && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClear}
                        sx={{
                          padding: "4px",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          "&:hover": {
                            backgroundColor: theme[0] === "dark" 
                              ? "rgba(255, 255, 255, 0.1)" 
                              : "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ClearIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Searchable Fields Info */}
              <Box
                sx={{
                  marginTop: "12px",
                  padding: "10px 12px",
                  backgroundColor: theme[0] === "dark" ? "rgba(107, 114, 128, 0.1)" : "rgba(107, 114, 128, 0.05)",
                  borderRadius: "6px",
                  border: `1px solid ${theme[0] === "dark" ? "rgba(107, 114, 128, 0.2)" : "rgba(107, 114, 128, 0.15)"}`,
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                    fontWeight: "500",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Searchable fields:
                </span>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                  }}
                >
                  {["Pair", "Direction", "Outcome", "Grade", "Alpha Pattern", "Beta Pattern", "Watchlist", "Entry No", "Entry Type"].map((field) => (
                    <span
                      key={field}
                      style={{
                        fontSize: "11px",
                        color: theme[0] === "dark" ? "#c9d1d9" : "#4b5563",
                        backgroundColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        border: `1px solid ${theme[0] === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                      }}
                    >
                      {field}
                    </span>
                  ))}
                </Box>
              </Box>

              {/* Performance Star Filters */}
              <Box
                sx={{
                  marginTop: "12px",
                  marginBottom: "12px",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: "8px", sm: "0" },
                  alignItems: { sm: "flex-start" },
                  justifyContent: { sm: "space-between" },
                }}
              >
                {/* Execution Discipline */}
                <Box sx={{ flex: 0 }}>
                  <Box>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        display: "block",
                        marginBottom: "2px",
                        textAlign: "center",
                      }}
                    >
                      Execution
                    </span>
                    <Box sx={{ display: "flex", gap: "2px", transform: "scale(0.75)", transformOrigin: "center center", justifyContent: "center" }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Box
                          key={star}
                          onClick={(e) => handleStarClick('executionDiscipline', star, e)}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            transform: starFilters.executionDiscipline >= star ? "scale(1.05)" : "scale(1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform =
                              starFilters.executionDiscipline >= star ? "scale(1.05)" : "scale(1)";
                          }}
                        >
                          {getStarIcon(starFilters.executionDiscipline, star)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <CustomSelect
                    size="small"
                    value={starFilters.executionOperator || '>='}
                    onChange={(e) => onStarFiltersChange({
                      ...starFilters,
                      executionOperator: e.target.value
                    })}
                    menuItemArray={JSON.stringify([
                      { value: ">=", label: "At least" },
                      { value: "<=", label: "At most" },
                      { value: "=", label: "Exactly" }
                    ])}
                    variant="compact"
                    sx={{
                      marginTop: "4px",
                      height: "24px",
                      "& .MuiFormControl-root": {
                        height: "24px",
                      },
                    }}
                  />
                </Box>

                {/* Emotional Control */}
                <Box sx={{ flex: 0 }}>
                  <Box>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        display: "block",
                        marginBottom: "2px",
                        textAlign: "center",
                      }}
                    >
                      Emotional
                    </span>
                    <Box sx={{ display: "flex", gap: "2px", transform: "scale(0.75)", transformOrigin: "center center", justifyContent: "center" }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Box
                          key={star}
                          onClick={(e) => handleStarClick('emotionalControl', star, e)}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            transform: starFilters.emotionalControl >= star ? "scale(1.05)" : "scale(1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform =
                              starFilters.emotionalControl >= star ? "scale(1.05)" : "scale(1)";
                          }}
                        >
                          {getStarIcon(starFilters.emotionalControl, star)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <CustomSelect
                    size="small"
                    value={starFilters.emotionalOperator || '>='}
                    onChange={(e) => onStarFiltersChange({
                      ...starFilters,
                      emotionalOperator: e.target.value
                    })}
                    menuItemArray={JSON.stringify([
                      { value: ">=", label: "At least" },
                      { value: "<=", label: "At most" },
                      { value: "=", label: "Exactly" }
                    ])}
                    variant="compact"
                    sx={{
                      marginTop: "4px",
                      height: "24px",
                      "& .MuiFormControl-root": {
                        height: "24px",
                      },
                    }}
                  />
                </Box>

                {/* Setup Quality */}
                <Box sx={{ flex: 0 }}>
                  <Box>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        display: "block",
                        marginBottom: "2px",
                        textAlign: "center",
                      }}
                    >
                      Setup Quality
                    </span>
                    <Box sx={{ display: "flex", gap: "2px", transform: "scale(0.75)", transformOrigin: "center center", justifyContent: "center" }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Box
                          key={star}
                          onClick={(e) => handleStarClick('setupQuality', star, e)}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            transform: starFilters.setupQuality >= star ? "scale(1.05)" : "scale(1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform =
                              starFilters.setupQuality >= star ? "scale(1.05)" : "scale(1)";
                          }}
                        >
                          {getStarIcon(starFilters.setupQuality, star)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <CustomSelect
                    size="small"
                    value={starFilters.setupOperator || '>='}
                    onChange={(e) => onStarFiltersChange({
                      ...starFilters,
                      setupOperator: e.target.value
                    })}
                    menuItemArray={JSON.stringify([
                      { value: ">=", label: "At least" },
                      { value: "<=", label: "At most" },
                      { value: "=", label: "Exactly" }
                    ])}
                    variant="compact"
                    sx={{
                      marginTop: "4px",
                      height: "24px",
                      "& .MuiFormControl-root": {
                        height: "24px",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Search Results Count */}
              {filteredCount !== null && totalCount !== null && localValue && (
                <Box
                  sx={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    backgroundColor: theme[0] === "dark" ? "rgba(28, 131, 222, 0.1)" : "rgba(37, 99, 235, 0.1)",
                    borderRadius: "6px",
                    border: `1px solid ${theme[0] === "dark" ? "rgba(28, 131, 222, 0.3)" : "rgba(37, 99, 235, 0.3)"}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                      fontWeight: "500",
                    }}
                  >
                    {filteredCount} of {totalCount} trades match
                  </span>
                </Box>
              )}
            </Box>
          ) : (
            // Advanced Filter Tab
            <Box sx={{ padding: "16px 20px" }}>
              <MultiFilterPanel
                filters={multiFilters}
                onFiltersChange={onMultiFiltersChange}
                logicOperator={logicOperator}
                onLogicOperatorChange={onLogicOperatorChange}
              />
            </Box>
          )}

          
        </Box>
      )}

      {/* Overlay backdrop */}
      {isOpen && (
        <Box
          onClick={handleClose}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            zIndex: 998,
          }}
        />
      )}
    </>
  );
};

export default FloatingSearch;
